<template>
    <Column
        v-bind="$attrs"
        :field="field"
        :header="header"
        :pt="pt"
        :sortable="true"
        :class="[
            'fm-table__column',
            extraClass ? `fm-table__column--${extraClass}` : '',
            { 'fm-table__column--centred': centred },
        ]"
    >
        <template #sorticon="{ sorted, sortOrder }">
            <FontAwesomeIcon
                v-if="sorted && sortOrder === 1"
                :icon="faArrowUp"
                aria-label="Sorted ascending"
            />
            <FontAwesomeIcon
                v-else-if="sorted && sortOrder === -1"
                :icon="faArrowDown"
                aria-label="Sorted descending"
            />
        </template>
        <template #body="slotProps">
            <Skeleton
                v-if="loading"
                :width="loadingWidth ?? '100%'"
                data-testid="loading"
                height="16px"
            />
            <slot v-else-if="$slots.body" name="body" v-bind="slotProps" />
            <span v-else>{{ slotProps.data[field] ?? '' }}</span>
        </template>
    </Column>
</template>

<script setup lang="ts">
import Column from 'primevue/column'
import { FontAwesomeIcon } from 'fontawesome/vue-fontawesome'
import { faArrowDown, faArrowUp } from 'fontawesome/pro-regular-svg-icons'
import Skeleton from 'primevue/skeleton'

interface Props {
    field: string
    header: string
    loading: boolean
    extraClass?: string
    centred?: boolean
    loadingWidth?: string
}
const props = defineProps<Props>()

const pt = {
    headerCell: { class: 'fm-table__header' },
    columnTitle: { class: 'fm-table__header__column-title' },
    columnHeaderContent: { class: 'fm-table__header__content' },
    sort: { class: 'fm-table__header__sort' },
}
</script>

<style lang="less">
// Styles are unscoped to target PrimeVue Table classes

@import '~@/styles/prime-vue/variables.less';
@import '~@/styles/rain/variables.less';

.fm-table__column {
    padding: 16px @spacing-xl;
    font-weight: @fontWeight-medium;
    font-size: @fontSize-md;
    line-height: @lineHeight-md;

    &--display-name {
        color: var(--p-primary-700);
    }

    &--responses {
        font-weight: @fontWeight-regular;
        font-size: @fontSize-base;
    }

    &--score {
        white-space: nowrap;

        &__current {
            display: inline-block;
            padding-right: @spacing-md;
        }

        &__change {
            display: inline-block;

            &--positive {
                color: var(--p-success-600);
            }
            &--neutral {
                color: var(--p-gray-500);
            }
            &--negative {
                color: var(--p-error-600);
            }

            &__icon {
                width: 14px;
                padding-right: 2px;
            }
            &__number {
                font-size: @fontSize-sm;
                line-height: @fontSize-sm;
                letter-spacing: @letterSpacing-xs;
            }
        }
    }

    .p-skeleton {
        margin: 4px 0;
    }
}

.fm-table__column--centred {
    text-align: center;

    .fm-table__header__content {
        justify-content: center;
    }

    .p-skeleton {
        margin: auto;
    }
}

.fm-table__header {
    padding: 12px @spacing-xl;
    text-transform: uppercase;
    color: var(--p-gray-600);
    background: var(--p-gray-50);
    border-top: 1px solid var(--p-gray-200);
    border-bottom: 1px solid var(--p-gray-200);
    font-size: 10px;
    line-height: @lineHeight-sm;

    &__column-title {
        font-weight: @fontWeight-medium;
    }

    &__sort:empty {
        display: none;
    }
}
</style>
